import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    CircularProgress,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LineChart } from "@mui/x-charts";
import { useCookies } from "react-cookie";
import { CARD_STYLES, FONTS } from "../../../constants/styles";
import WorkAnalyticsApi, {
    WorkAnalyticsData,
} from "../../../api/WorkAnalytics";
import { PeriodSelection } from "./types";

interface Props {
    selection: PeriodSelection;
}

const formatHours = (minutes: number): string => {
    const hours = minutes / 60;
    return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
};

// The revenue comparison (chart + delta) is period-scaled server-side
// (prev day/week/month/year), so the label follows the selected period.
const prevPeriodLabel = (period: PeriodSelection["period"]): string => {
    switch (period) {
        case "day":
            return "vs yesterday";
        case "week":
            return "vs last week";
        case "month":
            return "vs last month";
        case "year":
            return "vs last year";
        default:
            return "vs previous period";
    }
};

const SECTION_LABEL_SX = {
    fontSize: "11px",
    fontWeight: 600,
    color: "text.secondary",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
} as const;

// One statement line: label wraps freely on the left (long RU text gets a
// second line instead of clipping), the value is pinned right and never wraps
// (it's a number). This is what makes the whole screen RU-safe by construction.
const StatementRow = ({ label, value }: { label: string; value: string }) => (
    <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={2}
        sx={{
            py: 0.9,
            borderBottom: "1px solid rgba(0,0,0,0.05)",
            "&:last-of-type": { borderBottom: "none", pb: 0 },
        }}
    >
        <Typography fontSize="13px" color="text.secondary" sx={{ minWidth: 0 }}>
            {label}
        </Typography>
        <Typography
            className="no-translation"
            fontFamily={FONTS.display}
            fontWeight={700}
            fontSize="16px"
            noWrap
            sx={{ flex: "0 0 auto" }}
        >
            {value}
        </Typography>
    </Stack>
);

const WorkAnalytics: React.FC<Props> = ({ selection }) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [data, setData] = useState<WorkAnalyticsData | undefined>();
    const [loading, setLoading] = useState(true);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!uuid || !brand_id || !location_id) return;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        WorkAnalyticsApi.info(uuid, brand_id, location_id, selection)
            .then((res) => {
                if (requestId !== requestIdRef.current) return; // stale
                setData(res);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setLoading(false);
            });
        // `selection` is memoized in the parent (stable per period/from/to).
    }, [uuid, brand_id, location_id, selection]);

    if (loading && !data) {
        return (
            <Stack alignItems="center" py={4}>
                <CircularProgress size={28} />
            </Stack>
        );
    }

    if (!data) {
        return (
            <Stack sx={CARD_STYLES} spacing={1}>
                <Typography fontSize="14px" color="text.secondary">
                    No analytics yet — your numbers appear as bookings come in.
                </Typography>
            </Stack>
        );
    }

    const { revenue, time, comparison, forecast } = data;
    const occupancyPct = time.occupancy;
    const up = comparison.deltaPct != null && comparison.deltaPct >= 0;

    // Abbreviate axis labels — full RU month names ("Февраль") overlap into
    // mush on a phone-width axis; three letters read cleanly.
    const shortLabel = (v: string | number) => {
        const s = String(v ?? "");
        return s.length > 4 ? s.slice(0, 3) : s;
    };

    return (
        <Stack className="force-dynamic-translation" spacing={2} width="100%">
            {/* ── Revenue hero — the signature ── */}
            <Stack
                sx={{
                    ...CARD_STYLES,
                    background: `linear-gradient(135deg, ${alpha(
                        primary,
                        0.1
                    )} 0%, ${alpha(primary, 0.02)} 100%)`,
                }}
                spacing={1.25}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                >
                    <Typography sx={SECTION_LABEL_SX}>Revenue</Typography>
                    {comparison.deltaPct !== null && (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="baseline"
                            sx={{
                                bgcolor: alpha(
                                    up
                                        ? theme.palette.success.main
                                        : theme.palette.error.main,
                                    0.12
                                ),
                                borderRadius: "10px",
                                px: 1,
                                py: 0.35,
                                flex: "0 0 auto",
                            }}
                        >
                            <Typography
                                className="no-translation"
                                fontSize="12px"
                                fontWeight={700}
                                color={up ? "success.main" : "error.main"}
                            >
                                {up ? "+" : ""}
                                {Math.round(comparison.deltaPct)}%
                            </Typography>
                            <Typography
                                fontSize="11px"
                                color="text.secondary"
                                noWrap
                            >
                                {prevPeriodLabel(selection.period)}
                            </Typography>
                        </Stack>
                    )}
                </Stack>

                <Typography
                    className="no-translation"
                    fontFamily={FONTS.serif}
                    fontWeight={500}
                    fontSize="52px"
                    color={primary}
                    lineHeight={1}
                    sx={{
                        fontVariantNumeric: "tabular-nums",
                        letterSpacing: "-0.015em",
                    }}
                >
                    €{Math.round(revenue.total)}
                </Typography>

                <Stack spacing={0} pt={0.5}>
                    <StatementRow
                        label="Average per working day"
                        value={`€${Math.round(revenue.avgPerDay)}`}
                    />
                    <StatementRow
                        label="Average per booking"
                        value={`€${Math.round(revenue.avgPerBooking)}`}
                    />
                    <StatementRow
                        label="Best day"
                        value={
                            revenue.bestDayOfWeek
                                ? revenue.bestDayOfWeek.label
                                : "—"
                        }
                    />
                </Stack>
            </Stack>

            {/* ── Trend ── */}
            <Stack sx={CARD_STYLES} spacing={0.5}>
                <Typography sx={SECTION_LABEL_SX}>
                    Trend {prevPeriodLabel(selection.period)}
                </Typography>
                <Box sx={{ width: "100%", overflow: "hidden" }}>
                    <LineChart
                        height={190}
                        margin={{ top: 20, bottom: 24, left: 40, right: 10 }}
                        xAxis={[
                            {
                                scaleType: "point",
                                data: comparison.labels,
                                valueFormatter: shortLabel,
                                tickLabelStyle: { fontSize: 10 },
                            },
                        ]}
                        yAxis={[{ tickLabelStyle: { fontSize: 10 } }]}
                        series={[
                            {
                                data: comparison.current,
                                label: "This period",
                                color: primary,
                                curve: "monotoneX",
                                showMark: false,
                                valueFormatter: (v) =>
                                    v == null ? "" : `€${Math.round(v)}`,
                            },
                            {
                                data: comparison.previous,
                                label: "Previous",
                                color: alpha(primary, 0.35),
                                curve: "monotoneX",
                                connectNulls: true,
                                showMark: false,
                                valueFormatter: (v) =>
                                    v == null ? "" : `€${Math.round(v)}`,
                            },
                        ]}
                        slotProps={{
                            legend: {
                                labelStyle: { fontSize: 11 },
                                itemMarkWidth: 10,
                                itemMarkHeight: 10,
                            },
                        }}
                    />
                </Box>
            </Stack>

            {/* ── Time & occupancy ── */}
            <Stack sx={CARD_STYLES} spacing={1.25}>
                <Typography sx={SECTION_LABEL_SX}>Time</Typography>
                <Stack spacing={0}>
                    <StatementRow
                        label="Hours worked"
                        value={formatHours(time.hoursReceived)}
                    />
                    <StatementRow
                        label="Personal time"
                        value={formatHours(time.hoursBlocked)}
                    />
                    <StatementRow
                        label="Value of your hour"
                        value={
                            time.avgHourly != null
                                ? `€${Math.round(time.avgHourly)}`
                                : "—"
                        }
                    />
                </Stack>
                <Stack spacing={0.6} pt={0.25}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography fontSize="12px" color="text.secondary">
                            Occupancy
                        </Typography>
                        <Typography
                            className="no-translation"
                            fontSize="12px"
                            fontWeight={700}
                            color="text.primary"
                        >
                            {occupancyPct != null
                                ? `${Math.round(occupancyPct)}%`
                                : "—"}
                        </Typography>
                    </Stack>
                    <Box
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: alpha(primary, 0.1),
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                height: "100%",
                                // Bar capped at 100% even when real occupancy exceeds it.
                                width: `${Math.min(100, occupancyPct ?? 0)}%`,
                                bgcolor: primary,
                                borderRadius: 4,
                                transition: "width 0.4s ease",
                            }}
                        />
                    </Box>
                </Stack>
            </Stack>

            {/* ── Forecast ── */}
            <Stack sx={CARD_STYLES} spacing={0.75}>
                <Typography sx={SECTION_LABEL_SX}>Forecast</Typography>
                {!forecast.enoughData ? (
                    <Typography fontSize="13px" color="text.secondary">
                        Not enough history yet — a few more weeks of bookings and
                        this fills in.
                    </Typography>
                ) : (
                    <>
                        <Stack
                            direction="row"
                            alignItems="baseline"
                            spacing={0.75}
                            flexWrap="wrap"
                        >
                            <Typography
                                className="no-translation"
                                fontFamily={FONTS.serif}
                                fontWeight={500}
                                fontSize="32px"
                                color={primary}
                                lineHeight={1}
                                sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                                €{Math.round(forecast.projectedTotal)}
                            </Typography>
                            <Typography fontSize="12px" color="text.secondary">
                                projected this period
                            </Typography>
                        </Stack>
                        {forecast.comparison && (
                            <Typography
                                fontSize="12px"
                                color="text.secondary"
                            >
                                vs last month:
                                {forecast.comparison.revenueDeltaPct !==
                                    null && (
                                    <>
                                        {" "}
                                        revenue{" "}
                                        <span className="no-translation">
                                            {forecast.comparison
                                                .revenueDeltaPct >= 0
                                                ? "+"
                                                : ""}
                                            {Math.round(
                                                forecast.comparison
                                                    .revenueDeltaPct
                                            )}
                                            %
                                        </span>
                                    </>
                                )}
                                {forecast.comparison.bookingsDeltaPct !==
                                    null && (
                                    <>
                                        {" "}
                                        · bookings{" "}
                                        <span className="no-translation">
                                            {forecast.comparison
                                                .bookingsDeltaPct >= 0
                                                ? "+"
                                                : ""}
                                            {Math.round(
                                                forecast.comparison
                                                    .bookingsDeltaPct
                                            )}
                                            %
                                        </span>
                                    </>
                                )}
                            </Typography>
                        )}
                    </>
                )}
            </Stack>
        </Stack>
    );
};

export default WorkAnalytics;
